import React from "react";
import {
  Paper,
  Box,
  Typography,
  Stack,
  IconButton,
  Tooltip,
  Button,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LastPageIcon from "@mui/icons-material/LastPage";

interface ReportsPaginationProps {
  page: number;
  totalReports: number;
  rowsPerPage: number;
  onPageChange: (newPage: number) => void;
  onRowsPerPageChange: (event: SelectChangeEvent<number>) => void;
}

export const ReportsPagination: React.FC<ReportsPaginationProps> = ({
  page,
  totalReports,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const totalPages = totalReports ? Math.ceil(totalReports / rowsPerPage) : 0;
  const resultsStart = totalReports === 0 ? 0 : page * rowsPerPage + 1;
  const resultsEnd =
    totalReports === 0 ? 0 : Math.min((page + 1) * rowsPerPage, totalReports);

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages + 2) {
      for (let i = 0; i < totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(0);
      if (page > 2) pages.push("...");
      const start = Math.max(1, page - 1);
      const end = Math.min(totalPages - 2, page + 1);
      for (let i = start; i <= end; i++) {
        if (!pages.includes(i)) pages.push(i);
      }
      if (page < totalPages - 3) pages.push("...");
      if (!pages.includes(totalPages - 1)) pages.push(totalPages - 1);
    }
    return pages;
  };

  if (totalReports === 0) return null;

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 3,
        mb: 2,
        p: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "grey.400",
        bgcolor: "background.paper",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "center" },
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        {/* Results info */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            textAlign: { xs: "center", md: "left" },
            order: { xs: 2, md: 1 },
          }}
        >
          Showing{" "}
          <Box component="span" fontWeight="bold" color="text.primary">
            {resultsStart}
          </Box>
          {" - "}
          <Box component="span" fontWeight="bold" color="text.primary">
            {resultsEnd}
          </Box>
          {" of "}
          <Box component="span" fontWeight="bold" color="text.primary">
            {totalReports}
          </Box>
          {" reports"}
        </Typography>

        {/* Page navigation */}
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          justifyContent="center"
          sx={{ order: { xs: 1, md: 2 } }}
        >
          <Tooltip title="First page">
            <span>
              <IconButton
                onClick={() => onPageChange(0)}
                disabled={page === 0}
                size="small"
                sx={{
                  border: "1px solid",
                  borderColor: "grey.400",
                  borderRadius: 1,
                  "&:hover": { bgcolor: "action.hover" },
                  "&.Mui-disabled": { opacity: 0.5 },
                }}
              >
                <FirstPageIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Previous page">
            <span>
              <IconButton
                onClick={() => onPageChange(page - 1)}
                disabled={page === 0}
                size="small"
                sx={{
                  border: "1px solid",
                  borderColor: "grey.400",
                  borderRadius: 1,
                  "&:hover": { bgcolor: "action.hover" },
                  "&.Mui-disabled": { opacity: 0.5 },
                }}
              >
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          {/* Page numbers */}
          <Stack
            direction="row"
            spacing={0.5}
            sx={{ display: { xs: "none", sm: "flex" } }}
          >
            {getPageNumbers().map((pageNum, index) =>
              pageNum === "..." ? (
                <Typography
                  key={`ellipsis-${index}`}
                  variant="body2"
                  sx={{ px: 1, alignSelf: "center" }}
                  color="text.secondary"
                >
                  ...
                </Typography>
              ) : (
                <Button
                  key={pageNum}
                  onClick={() => onPageChange(pageNum as number)}
                  variant={page === pageNum ? "contained" : "outlined"}
                  size="small"
                  sx={{
                    minWidth: 36,
                    height: 36,
                    p: 0,
                    borderRadius: 1,
                    fontWeight: page === pageNum ? "bold" : "normal",
                    ...(page !== pageNum && {
                      borderColor: "grey.400",
                      color: "text.primary",
                      "&:hover": {
                        bgcolor: "action.hover",
                        borderColor: "primary.main",
                      },
                    }),
                  }}
                >
                  {(pageNum as number) + 1}
                </Button>
              )
            )}
          </Stack>

          {/* Mobile page indicator */}
          <Typography
            variant="body2"
            sx={{
              display: { xs: "block", sm: "none" },
              px: 2,
              fontWeight: "medium",
            }}
          >
            {page + 1} / {totalPages || 1}
          </Typography>

          <Tooltip title="Next page">
            <span>
              <IconButton
                onClick={() => onPageChange(page + 1)}
                disabled={page >= totalPages - 1}
                size="small"
                sx={{
                  border: "1px solid",
                  borderColor: "grey.400",
                  borderRadius: 1,
                  "&:hover": { bgcolor: "action.hover" },
                  "&.Mui-disabled": { opacity: 0.5 },
                }}
              >
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Last page">
            <span>
              <IconButton
                onClick={() => onPageChange(totalPages - 1)}
                disabled={page >= totalPages - 1}
                size="small"
                sx={{
                  border: "1px solid",
                  borderColor: "grey.400",
                  borderRadius: 1,
                  "&:hover": { bgcolor: "action.hover" },
                  "&.Mui-disabled": { opacity: 0.5 },
                }}
              >
                <LastPageIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>

        {/* Rows per page selector */}
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent={{ xs: "center", md: "flex-end" }}
          sx={{ order: 3 }}
        >
          <Typography variant="body2" color="text.secondary">
            Per page:
          </Typography>
          <FormControl size="small">
            <Select
              value={rowsPerPage}
              onChange={onRowsPerPageChange}
              sx={{
                minWidth: 70,
                height: 36,
                "& .MuiSelect-select": { py: 0.75 },
              }}
            >
              {[5, 10, 25, 50].map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Box>
    </Paper>
  );
};
